if (!customElements.get("video-reels")) {
  customElements.define(
    "video-reels",
    class VideoReels extends HTMLElement {
      constructor() {
        super();
        this.activeClass = "active";
        this.swiper = null;
      }

      connectedCallback() {
        this.swiperEl = this.querySelector(".wt-slider__container");
        this.handlePlayPauseClick = this.handlePlayPauseClick.bind(this);
        this.addEventListener("click", this.handlePlayPauseClick);
        this.init();
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.handlePlayPauseClick);
      }

      parseSourcesFromWrapper(wrapper) {
        const raw = wrapper?.dataset?.videoSources || "";
        if (!raw) return [];
        try {
          const arr = JSON.parse(raw);
          return Array.isArray(arr) ? arr.filter((s) => s?.url) : [];
        } catch {
          return [];
        }
      }

      createVideoElement(sources) {
        if (!sources.length) return null;

        const video = document.createElement("video");
        video.className = "video-reel__video wt-video__movie";
        video.playsInline = true;
        video.loop = true;
        video.muted = true;
        video.controls = false;
        video.preload = "auto";

        sources.forEach((s) => {
          const source = document.createElement("source");
          source.src = s.url;
          source.type = s.type || "video/mp4";
          video.appendChild(source);
        });

        return video;
      }

      handlePlayPauseClick(e) {
        const btn = e.target.closest(".video-reel__play-pause-btn");
        if (!btn) return;

        const wrapper = btn.closest(".video-reel__poster-wrapper[data-video-sources]");
        if (!wrapper) return;

        e.preventDefault();
        e.stopPropagation();

        const sources = this.parseSourcesFromWrapper(wrapper);
        let video = wrapper.querySelector(".video-reel__video");
        const playPauseBtn = wrapper.querySelector(".video-reel__play-pause-btn");
        const soundToggle = wrapper.querySelector(".video-reel__sound-toggle");
        const poster = wrapper.querySelector(".video-reel__poster, .video-reel__poster--placeholder");

        if (!video) {
          video = this.createVideoElement(sources);
          if (!video) return;
          wrapper.appendChild(video);

          video.addEventListener("play", () => this.syncPlayPauseIcon(wrapper));
          video.addEventListener("pause", () => this.syncPlayPauseIcon(wrapper));
          video.addEventListener("ended", () => {
            video.style.display = "none";
            video.currentTime = 0;
            wrapper.classList.remove("is-playing");
            if (playPauseBtn) {
              playPauseBtn.classList.add("shoppable-video__control-button--pause");
              playPauseBtn.classList.remove("shoppable-video__control-button--play");
            }
            if (poster) poster.style.display = "";
          });
        }

        if (video.paused) {
          if (poster) poster.style.display = "none";
          wrapper.classList.add("is-playing");
          if (soundToggle) soundToggle.style.display = "flex";
          video.style.display = "block";

          if (this.swiperEl) this.swiperEl.dataset.sound = "on";
          video.muted = false;

          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch(() => {
              video.muted = true;
              if (this.swiperEl) this.swiperEl.dataset.sound = "off";
              video.play().catch((err) => console.warn("Video play failed:", err));
            });
          }
        } else {
          video.pause();
        }
      }

      syncPlayPauseIcon(wrapper) {
        const video = wrapper?.querySelector(".video-reel__video");
        const btn = wrapper?.querySelector(".video-reel__play-pause-btn");
        if (!video || !btn) return;
        const isPlaying = !video.paused;
        btn.classList.toggle("shoppable-video__control-button--play", isPlaying);
        btn.classList.toggle("shoppable-video__control-button--pause", !isPlaying);
      }

      updateAllVideosSound(el) {
        const soundOn = el?.dataset?.sound === "on";
        this.querySelectorAll(".video-reel__video").forEach((video) => {
          video.muted = !soundOn;
        });
      }

      bindSoundToggles() {
        const container = this.swiperEl;
        if (!container) return;

        this.querySelectorAll(".video-reel__sound-toggle").forEach((button) => {
          if (button.dataset.bound) return;
          button.dataset.bound = "true";
          button.addEventListener("click", (e) => {
            e.stopPropagation();
            const isOn = container.dataset.sound === "on";
            container.dataset.sound = isOn ? "off" : "on";
            this.updateAllVideosSound(container);
          });
        });
      }

      pauseAllVideos() {
        this.querySelectorAll(".video-reel__video").forEach((v) => v.pause());
      }

      playVideoInActiveSlide(swiper) {
        if (!swiper?.slides) return;
        const activeSlide = swiper.slides[swiper.activeIndex];
        if (!activeSlide) return;

        const wrapper = activeSlide.querySelector(".video-reel__poster-wrapper[data-video-sources]");
        const video = wrapper?.querySelector(".video-reel__video");
        if (video && video.style.display !== "none") {
          video.muted = this.swiperEl?.dataset?.sound !== "on";
          video.play().catch(() => {});
        }
      }

      findActiveSlide(swiper) {
        return swiper?.slides?.[swiper.activeIndex];
      }

      toggleActiveClass(swiper) {
        const activeSlide = this.findActiveSlide(swiper);
        swiper?.slides?.forEach((slide) => {
          slide.classList.toggle(this.activeClass, slide === activeSlide);
        });
      }

      handleSlideChange(swiper) {
        this.pauseAllVideos();
        this.toggleActiveClass(swiper);
        this.playVideoInActiveSlide(swiper);
      }

      observeSection() {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                this.pauseAllVideos();
              } else if (this.swiper) {
                this.playVideoInActiveSlide(this.swiper);
              }
            });
          },
          { root: null, rootMargin: "0px", threshold: 0.1 }
        );
        observer.observe(this);
      }

      checkSwiperInitialization() {
        const swiperContainer = this.swiperEl;
        const swiperInstance = swiperContainer?.swiper;

        if (swiperContainer?.classList?.contains("swiper-initialized")) {
          this.swiper = swiperInstance;
          this.addVideoEventHandlers(swiperInstance);
          this.toggleActiveClass(swiperInstance);
          this.bindSoundToggles();
          this.observeSection();
          clearInterval(this.checkInterval);
        }
      }

      addVideoEventHandlers(swiper) {
        if (!swiper) return;
        swiper.on("slideChange", (swp) => this.handleSlideChange(swp));
      }

      init() {
        this.checkInterval = setInterval(
          this.checkSwiperInitialization.bind(this),
          500
        );
      }
    }
  );
}
